import { COMPANY, CONTACT_PATH, LEGAL_PATHS } from './company';

export type LegalBlock = {
  heading: string;
  paragraphs?: string[];
  bullets?: string[];
};

export type LegalDocument = {
  id: 'terms' | 'privacy' | 'cookies' | 'refunds';
  path: string;
  title: string;
  intro: string;
  /**
   * Document title for the tab and the search result. Longer than `title`, which
   * is the heading on the page itself, since it also has to say whose policy this
   * is and what it covers.
   */
  metaTitle: string;
  /**
   * Search-result description. Separate from `intro`, which is written for
   * someone already reading the policy and runs far past the ~155 characters a
   * result snippet shows.
   */
  metaDescription: string;
  sections: LegalBlock[];
};

const contactLines = [
  `${COMPANY.legalName} (CIN: ${COMPANY.cin})`,
  `Email: ${COMPANY.supportEmail} (support) / ${COMPANY.legalEmail} (legal & privacy)`,
  `Phone: ${COMPANY.phone}`,
  `Contact page: ${COMPANY.siteUrl}${CONTACT_PATH}`,
];

export const LEGAL_DOCUMENTS: LegalDocument[] = [
  {
    id: 'terms',
    path: LEGAL_PATHS.terms,
    title: 'Terms & Conditions',
    metaTitle: 'Terms & Conditions | OnTheWeb AI Website Builder',
    metaDescription:
      'Understand the terms of using OnTheWeb before getting started, including your account responsibilities, content ownership, payments and subscriptions.',
    intro: `These Terms & Conditions (“Terms”) govern your access to and use of ${COMPANY.brand} (the “Service”), an AI website-builder operated by ${COMPANY.legalName} (“we”, “us”, “our”). By creating an account, paying for a plan, or using the Service, you agree to these Terms.`,
    sections: [
      {
        heading: '1. Who we are',
        paragraphs: [
          `${COMPANY.legalName} is a private limited company incorporated in India (CIN ${COMPANY.cin}). The Service is offered from India to businesses in India and internationally.`,
        ],
        bullets: contactLines,
      },
      {
        heading: '2. The Service',
        paragraphs: [
          `${COMPANY.brand} lets you describe a business (by text or voice) and generate a website, then edit, publish, and manage that website from your dashboard. The Service is a digital software-as-a-service product. No physical goods are sold or shipped.`,
          'We may add, change, or discontinue features. Free and paid plans, and the features included in each, are described on our website and at checkout. We may update plan details with reasonable notice for paid subscribers.',
        ],
      },
      {
        heading: '3. Eligibility and accounts',
        paragraphs: [
          'You must be at least 18 years old and able to form a binding contract. You are responsible for the accuracy of the information you provide (including phone number, name, and business details) and for keeping your account secure.',
          'One person or business may hold an account. You must not share login access in a way that circumvents plan limits, resell the Service without our written consent, or use the Service for anyone other than your own business except as a plan expressly allows.',
        ],
      },
      {
        heading: '4. Acceptable use',
        paragraphs: [
          'You must not use the Service to generate or host websites or content that is unlawful, defamatory, fraudulent, infringing, adult/CSAM, hateful, or that promotes scams, malware, or unauthorised access to systems. You must not attempt to reverse-engineer, overload, or disrupt the Service, or to bypass usage limits.',
          'You are solely responsible for the websites you create, including claims you make about your business, products, and prices, and for complying with advertising, consumer-protection, and tax laws that apply to you.',
        ],
      },
      {
        heading: '5. Your content and AI-generated output',
        paragraphs: [
          'You retain rights in the business information, prompts, images, and other materials you submit (“Customer Content”). You grant us a worldwide licence to host, process, and display Customer Content solely to operate, secure, and improve the Service (including generating and publishing your website).',
          'Websites and copy produced by the Service are generated by automated systems. You should review and edit them before publishing. Output may be similar to output generated for other customers. We do not warrant that generated content is unique, accurate, or fit for a particular legal or marketing purpose.',
          'You must have the rights to any images, logos, or text you upload. Do not submit personal data of other people unless you have a lawful basis to do so.',
        ],
      },
      {
        heading: '6. Fees, taxes, and payment',
        paragraphs: [
          'Paid plans are charged in the currency shown at checkout. Prices may be exclusive of applicable taxes (including GST), which will be added where required by law and shown before you pay.',
          'Payments are processed by third-party payment gateways. We do not store your full card or UPI credentials. By paying, you authorise the gateway and us to charge the selected plan, including recurring subscription charges until you cancel.',
          'If a payment fails, we may retry, suspend publishing, or downgrade the account after notice. You remain responsible for unpaid fees already incurred.',
        ],
      },
      {
        heading: '7. Subscriptions, cancellation, and refunds',
        paragraphs: [
          `Subscriptions renew automatically at the then-current rate unless you cancel. Cancellation, cooling-off, and refund rules are set out in our Refund & Cancellation Policy at ${COMPANY.siteUrl}${LEGAL_PATHS.refunds}, which forms part of these Terms.`,
        ],
      },
      {
        heading: '8. Intellectual property',
        paragraphs: [
          `The Service, including software, design, trademarks (including “${COMPANY.brand}”), and documentation, is owned by ${COMPANY.legalName} or its licensors. These Terms do not transfer our IP to you.`,
          'Subject to these Terms and your plan, we grant you a limited, non-exclusive, non-transferable licence to use the Service to create and operate websites for your business. You may not copy the platform, scrape our models, or use the Service to build a competing website-generator.',
        ],
      },
      {
        heading: '9. Third-party services',
        paragraphs: [
          'The Service may rely on hosting, AI, SMS, email, analytics, and payment providers. Their terms apply to those components. We are not responsible for outages or acts of third parties beyond our reasonable control.',
        ],
      },
      {
        heading: '10. Availability and support',
        paragraphs: [
          'We aim for reasonable uptime but do not guarantee uninterrupted or error-free service. Support is provided via email at the address below during Indian business hours unless a paid plan states otherwise.',
        ],
      },
      {
        heading: '11. Disclaimers',
        paragraphs: [
          'THE SERVICE AND ALL GENERATED WEBSITES ARE PROVIDED “AS IS” AND “AS AVAILABLE”. TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE DISCLAIM WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, AND THAT THE SERVICE WILL MEET YOUR REQUIREMENTS OR GENERATE A PARTICULAR ranking, sale, or design outcome.',
        ],
      },
      {
        heading: '12. Limitation of liability',
        paragraphs: [
          'To the maximum extent permitted by law, we are not liable for indirect, incidental, special, consequential, or punitive damages, or for lost profits, data, or goodwill.',
          'Our total liability arising out of the Service in any twelve-month period is limited to the fees you paid us for the Service in that period (or INR 5,000 if you are on a free plan). Nothing in these Terms limits liability that cannot be limited under Indian law (including fraud or personal injury caused by negligence).',
        ],
      },
      {
        heading: '13. Indemnity',
        paragraphs: [
          'You will indemnify and hold us harmless from claims, losses, and costs (including reasonable legal fees) arising from your websites, Customer Content, or your breach of these Terms or applicable law.',
        ],
      },
      {
        heading: '14. Suspension and termination',
        paragraphs: [
          'You may stop using the Service and cancel as described in the Refund & Cancellation Policy. We may suspend or terminate access immediately if you breach these Terms, if required by law, or if your use harms the Service or other customers. Upon termination we may delete or anonymise account data in line with our Privacy Policy, except where we must retain it for legal or accounting reasons.',
        ],
      },
      {
        heading: '15. Changes to these Terms',
        paragraphs: [
          'We may update these Terms. The “Last updated” date at the top of this page will change. Material changes to paid plans will be notified by email or in-product notice where reasonably possible. Continued use after the effective date constitutes acceptance.',
        ],
      },
      {
        heading: '16. Governing law and disputes',
        paragraphs: [
          `These Terms are governed by ${COMPANY.governingLaw}. Subject to any rights you have under mandatory consumer-protection law, ${COMPANY.jurisdiction} have exclusive jurisdiction.`,
          'Before filing a claim, please write to us so we can try to resolve the issue in good faith within 30 days.',
        ],
      },
      {
        heading: '17. Contact',
        paragraphs: ['For questions about these Terms:'],
        bullets: contactLines,
      },
    ],
  },
  {
    id: 'privacy',
    path: LEGAL_PATHS.privacy,
    title: 'Privacy Policy',
    metaTitle: 'Privacy Policy | How OnTheWeb Handles Your Data',
    metaDescription:
      'Know how your information is used when you create a website with OnTheWeb. Read about data collection, sharing, storage and your privacy choices.',
    intro: `This Privacy Policy explains how ${COMPANY.legalName} (“we”, “us”) collects, uses, stores, and shares personal data when you use ${COMPANY.brand} at ${COMPANY.siteUrl}. It is intended to meet requirements under the Information Technology Act, 2000, the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011, and the Digital Personal Data Protection Act, 2023, as they apply to us.`,
    sections: [
      {
        heading: '1. Data we collect',
        paragraphs: ['We collect:'],
        bullets: [
          'Account data: name, business name, phone number, email address, address if you provide it, and authentication records. When you ask to sign in or to confirm an email address, we send a one-time verification code to that address.',
          'Service data: prompts, voice recordings (transcribed and, where needed, translated), uploaded files and photos, website content you edit, and publish settings.',
          'Usage data: pages visited, device/browser type, approximate location derived from IP, and diagnostic logs.',
          'Payment data: plan, transaction identifiers, and payment status. Card, UPI, and net-banking details are collected by our payment gateway, not stored by us in full.',
          'Communications: emails or calls you send to support.',
        ],
      },
      {
        heading: '2. How we use data',
        paragraphs: ['We use personal data to:'],
        bullets: [
          'Create and authenticate your account, including a one-time verification code by SMS or email, sent only because you requested it.',
          'Generate, host, publish, and let you edit websites.',
          'Process payments, invoices, refunds, and prevent fraud.',
          'Provide support, security, and service improvement (including debugging AI generation).',
          'Send transactional messages you triggered, such as a verification code, a receipt, or a security alert. A newsletter is sent only if you subscribe to it, and you can opt out of marketing. Opting out does not stop a verification code you asked us to send.',
          'Comply with law, enforce our Terms, and handle disputes.',
        ],
      },
      {
        heading: '3. Legal bases',
        paragraphs: [
          'Where a legal basis is required, we rely on: performance of our contract with you; legitimate interests in operating a secure SaaS product; consent (for optional cookies or marketing); and legal obligation (tax, accounting, lawful requests).',
        ],
      },
      {
        heading: '4. Sharing',
        paragraphs: [
          'We do not sell personal data. We share it with processors who help us run the Service, under contracts that require them to protect it, including:',
        ],
        bullets: [
          'Cloud hosting and storage providers.',
          'AI model and transcription providers (to generate websites and transcribe voice).',
          'SMS and email delivery providers, including Amazon SES, used only to deliver a message you requested or a newsletter you subscribed to.',
          'Payment gateways and banks, to complete transactions and refunds.',
          'Professional advisers and authorities when required by law.',
        ],
      },
      {
        heading: '5. International transfers',
        paragraphs: [
          'Some processors may store or process data outside India. Where we do so, we take reasonable contractual and technical steps so that your data remains protected to a standard comparable with Indian law.',
        ],
      },
      {
        heading: '6. Retention',
        paragraphs: [
          'We keep account and website data while your account is active. After deletion or prolonged inactivity we delete or anonymise data within a reasonable period, unless we must retain it for tax, fraud-prevention, or legal claims (typically up to 8 years for financial records in India). Voice recordings are processed to text and are not kept longer than needed for that purpose and quality review.',
        ],
      },
      {
        heading: '7. Security',
        paragraphs: [
          'We use industry-standard measures such as encryption in transit, access controls, and least-privilege administration. No method of transmission or storage is completely secure. Please use a unique phone number you control and tell us promptly of any suspected unauthorised access.',
        ],
      },
      {
        heading: '8. Your rights',
        paragraphs: [
          'Subject to law, you may request access, correction, completeness, erasure, withdrawal of consent, and a copy of your data, or to nominate someone to exercise rights on your behalf. You may also complain to the Data Protection Board of India when that mechanism is available.',
          `To exercise rights, email ${COMPANY.legalEmail} from the address or phone associated with your account. We may need to verify your identity. We will respond within the time required by applicable law.`,
        ],
      },
      {
        heading: '9. Children',
        paragraphs: [
          'The Service is for businesses and adults. We do not knowingly collect personal data from children under 18. If you believe we have, contact us and we will delete it.',
        ],
      },
      {
        heading: '10. Cookies',
        paragraphs: [
          `We use cookies and similar technologies as described in our Cookie Policy at ${COMPANY.siteUrl}${LEGAL_PATHS.cookies}.`,
        ],
      },
      {
        heading: '11. Grievance officer',
        paragraphs: [
          'In accordance with the Information Technology Act, 2000 and applicable rules, the Grievance Officer for the Service is:',
        ],
        bullets: [
          COMPANY.grievanceOfficer,
          COMPANY.legalName,
          `Email: ${COMPANY.legalEmail}`,
          `Phone: ${COMPANY.phone}`,
        ],
      },
      {
        heading: '12. Changes',
        paragraphs: [
          'We may update this Policy. The “Last updated” date will change. Material changes will be highlighted on this page or notified to you where required.',
        ],
      },
      {
        heading: '13. Contact',
        bullets: contactLines,
      },
    ],
  },
  {
    id: 'cookies',
    path: LEGAL_PATHS.cookies,
    title: 'Cookie Policy',
    metaTitle: 'Cookie Policy | How Cookies Work on OnTheWeb',
    metaDescription:
      'Understand how OnTheWeb uses cookies for login, preferences and site functionality. Find out how to manage cookies and what happens when you block them.',
    intro: `This Cookie Policy explains how ${COMPANY.legalName} uses cookies and similar technologies on ${COMPANY.brand} (${COMPANY.siteUrl}). It should be read with our Privacy Policy.`,
    sections: [
      {
        heading: '1. What cookies are',
        paragraphs: [
          'Cookies are small text files stored on your device. We also use local storage and similar technologies for the same purposes described here. We refer to all of these as “cookies”.',
        ],
      },
      {
        heading: '2. How we use cookies',
        bullets: [
          'Strictly necessary: keep you signed in, protect the session, remember security settings, and load the Service. These are required for the site to work and do not require consent under typical cookie rules for essential operation.',
          'Preferences: remember theme (light/dark) and similar UI choices.',
          'Analytics: understand which pages are used so we can improve the product. We only use analytics cookies where they are configured on the live site.',
          'Payments: the payment gateway may set its own cookies on checkout to complete a transaction securely.',
        ],
      },
      {
        heading: '3. Duration',
        paragraphs: [
          'Session cookies expire when you close the browser. Persistent cookies remain until they expire or you delete them. Authentication tokens last for the session length we configure (and refresh while you stay signed in).',
        ],
      },
      {
        heading: '4. Third parties',
        paragraphs: [
          'Payment, analytics, and support tools may set cookies under their own policies. We do not control those cookies. Please review the gateway’s and any analytics provider’s notices at checkout or in their documentation.',
        ],
      },
      {
        heading: '5. Your choices',
        paragraphs: [
          'You can block or delete cookies in your browser settings. Blocking strictly necessary cookies will prevent sign-in and payments from working. Where we present a cookie banner, you can accept or reject non-essential cookies.',
          'Most browsers allow you to refuse third-party cookies. Do not track signals are honoured where we have implemented support for them.',
        ],
      },
      {
        heading: '6. Updates and contact',
        paragraphs: [
          `We may update this Policy when we add or change cookies. Questions: ${COMPANY.legalEmail}.`,
        ],
        bullets: contactLines,
      },
    ],
  },
  {
    id: 'refunds',
    path: LEGAL_PATHS.refunds,
    title: 'Refund & Cancellation Policy',
    metaTitle: 'Refund & Cancellation Policy | OnTheWeb Website Plans',
    metaDescription:
      'Planning to cancel your OnTheWeb subscription? Check cancellation steps, refund eligibility and payment timelines so you know what to expect before requesting.',
    intro: `This Refund & Cancellation Policy applies to all purchases of ${COMPANY.brand} made through ${COMPANY.siteUrl}. It is issued by ${COMPANY.legalName} and is intended to meet the requirements of Indian payment aggregators and card networks. ${COMPANY.brand} is a digital service. No physical products are shipped.`,
    sections: [
      {
        heading: '1. Plans and charges',
        paragraphs: [
          'The Service may include a free plan and paid subscription plans. Paid fees, billing interval (for example monthly), currency, and taxes (including GST where applicable) are shown before you confirm payment. Recurring subscriptions renew automatically until cancelled.',
        ],
      },
      {
        heading: '2. How to cancel',
        paragraphs: [
          'You may cancel a paid subscription at any time by:',
        ],
        bullets: [
          'Using the billing or plan controls in your dashboard, when available; or',
          `Emailing ${COMPANY.supportEmail} from the phone or email registered on the account, with the subject “Cancel subscription”, your registered name, and phone number.`,
        ],
      },
      {
        heading: '3. Effect of cancellation',
        paragraphs: [
          'Cancellation stops future renewals. You keep access to paid features until the end of the period you have already paid for. We do not provide partial-period credits for unused days after the cooling-off window described below, except where required by law or in the cases listed under “When we refund”.',
          'After the paid period ends, the account may revert to the free plan or publishing may be limited as described in the product at that time. Your website content remains yours; we may retain it for a limited time so you can export or resubscribe, then delete it in line with the Privacy Policy.',
        ],
      },
      {
        heading: '4. When we refund',
        paragraphs: ['We will refund:'],
        bullets: [
          'Duplicate or incorrect charges, and payments that failed to provision the Service because of a fault on our side.',
          'A first-time paid subscription cancelled within 7 (seven) days of the successful payment, if you have not substantially used paid features (for example, you have not generated or published a website under that paid plan).',
          'Any other refund required by applicable Indian consumer-protection law or by the payment gateway / card scheme rules.',
        ],
      },
      {
        heading: '5. When we do not refund',
        paragraphs: ['Except as required by law, we do not refund:'],
        bullets: [
          'Fees after the 7-day cooling-off period for a first paid purchase.',
          'Renewal charges when you cancel after a renewal has already been processed (access continues to the end of that period).',
          'Use of the Service where AI generation, publishing, custom domain, or other paid features have already been consumed in that billing period.',
          'Chargebacks initiated without first contacting us; we will share transaction records with the gateway and issuer. Unjustified chargebacks may lead to account suspension.',
        ],
      },
      {
        heading: '6. How refunds are paid',
        paragraphs: [
          'Approved refunds are issued only to the original payment method (card, UPI, net banking, or wallet) via our payment gateway. We do not refund in cash.',
          'Once we approve a refund, we initiate it within 5–7 working days. The bank, UPI app, or card network typically takes a further 5–10 working days to credit the amount. Exact timing depends on your issuer.',
        ],
      },
      {
        heading: '7. Failed, pending, or disputed payments',
        paragraphs: [
          'If an amount is debited but the plan is not activated, write to us with the transaction ID, date, amount, and registered phone number. We will reconcile with the gateway. If the payment succeeded on the gateway, we will activate the plan or refund as appropriate.',
        ],
      },
      {
        heading: '8. Delivery of the digital service',
        paragraphs: [
          'The Service is delivered online immediately after a successful payment (or, on the free plan, after registration). There is no shipping, delivery charge, or cash-on-delivery. This clause is the delivery / fulfilment policy for the digital Service.',
        ],
      },
      {
        heading: '9. How to request a refund',
        paragraphs: [
          `Email ${COMPANY.supportEmail} with the subject “Refund request”, including: registered name, phone number, transaction ID, date, amount, and reason. We aim to respond within 3 working days and to decide within 7 working days of receiving complete information.`,
        ],
      },
      {
        heading: '10. Contact',
        bullets: contactLines,
      },
    ],
  },
];

export function getLegalDocument(id: LegalDocument['id']): LegalDocument {
  return LEGAL_DOCUMENTS.find((d) => d.id === id)!;
}
